import { useMemo, useState } from "react";
import { Check, ArrowRight, ChevronDown, MessageCircle, Briefcase, Wallet, Sprout, Layers, Search, TrendingUp, Send, CreditCard, KeyRound, ShieldCheck } from "lucide-react";
import { Price, CtaButton, GuideMockup, useCountdown } from "./parts";
import { Wrap, FIT_ICONS } from "./SectionsTop";

function Chips({ label, options, value, onChange, name }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-[#5C6B73] mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map(([k, l]) => <button key={k} onClick={() => onChange(k)} className={`lp-chip ${value === k ? "lp-chip-on" : "lp-chip-off"}`} data-testid={`fit-${name}-${k}`}>{l}</button>)}
      </div>
    </div>
  );
}

export function Fit({ t, facets }) {
  const f = t.fit;
  const [budget, setBudget] = useState("any");
  const [time, setTime] = useState("any");
  const [level, setLevel] = useState("any");
  const count = useMemo(() => facets.filter((x) => (budget === "any" || x.budget <= Number(budget)) && (time === "any" || x[time]) && (level === "any" || x.beginner)).length, [facets, budget, time, level]);
  return (
    <section className="bg-[#EAF8F4]" data-testid="landing-fit">
      <Wrap className="py-16 md:py-24">
        <h2 className="lp-h2 max-w-3xl">{f.h}</h2>
        <p className="font-display font-bold text-[#0BAF9A] text-lg sm:text-xl mt-3">{f.sub}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-9">
          {f.cards.map(([title, q], i) => {
            const Icon = FIT_ICONS[i];
            return <div key={title} className="lp-card p-4 sm:p-5"><Icon className="h-6 w-6 text-[#0BAF9A]" /><div className="font-display font-bold text-[#102B3F] mt-3 leading-tight">{title}</div><p className="text-[13px] sm:text-sm text-[#5C6B73] mt-1 leading-snug">{q}</p></div>;
          })}
        </div>
        {facets.length > 0 && (
          <div className="lp-card p-5 sm:p-7 mt-6 grid md:grid-cols-[1fr_auto] gap-6 md:items-center" data-testid="fit-filter">
            <div className="space-y-4">
              <div className="font-display font-bold text-[#102B3F]">{f.try}</div>
              <Chips label={f.budget} options={f.budgets} value={budget} onChange={setBudget} name="budget" />
              <Chips label={f.time} options={f.times} value={time} onChange={setTime} name="time" />
              <Chips label={f.level} options={f.levels} value={level} onChange={setLevel} name="level" />
            </div>
            <div className="rounded-2xl bg-[#102B3F] text-white p-6 text-center md:w-56" data-testid="fit-result">
              <div className="font-display font-extrabold text-5xl text-[#5FE0CB]" data-testid="fit-count">{count}</div>
              <div className="text-xs text-white/60 mt-1">{f.of} {facets.length}</div>
              <div className="text-sm mt-2 leading-snug">{f.match}</div>
            </div>
          </div>
        )}
        <p className="mt-8 text-base sm:text-lg text-[#36495A] max-w-3xl">{f.close}</p>
      </Wrap>
    </section>
  );
}

export function Value({ t }) {
  const v = t.value;
  return (
    <section className="bg-white" data-testid="landing-value">
      <Wrap className="py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="lp-h2">{v.h}</h2>
          <p className="text-[#4A5D6A] mt-4 text-base sm:text-lg">{v.p}</p>
        </div>
        <div className="lp-card p-5 sm:p-7">
          <ul className="space-y-3">{v.list.map((x, i) => <li key={x} className="flex items-center gap-3 rounded-2xl bg-[#F4F8F9] px-4 py-3"><span className="h-7 w-7 rounded-lg bg-[#0BAF9A] text-white flex items-center justify-center shrink-0"><Check className="h-4 w-4" strokeWidth={3} /></span><span className="font-semibold text-[#102B3F] text-[15px]">{x}</span><span className="ml-auto text-[11px] font-bold text-[#7D8C96]">{i + 1}/6</span></li>)}</ul>
          <div className="mt-4 text-right font-display font-extrabold text-2xl text-[#102B3F]">{v.times}</div>
        </div>
        <p className="md:col-span-2 font-display font-bold text-lg sm:text-xl text-[#102B3F] border-l-4 border-[#0BAF9A] pl-5 max-w-3xl">{v.close}</p>
      </Wrap>
    </section>
  );
}

const WHO_ICONS = [Briefcase, Wallet, Sprout, Layers, Search, TrendingUp];
export function Who({ t }) {
  return (
    <section className="bg-[#EAF8F4]" data-testid="landing-who">
      <Wrap className="py-16 md:py-24">
        <h2 className="lp-h2">{t.who.h}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-9">
          {t.who.items.map(([title, d], i) => {
            const Icon = WHO_ICONS[i];
            return <div key={title} className="lp-card p-4 sm:p-5 flex gap-4" data-testid={`who-card-${i}`}><div className="h-11 w-11 rounded-2xl bg-[#EAF8F4] flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-[#088F7E]" /></div><div><div className="font-display font-bold text-[#102B3F] leading-tight">{title}</div><p className="text-sm text-[#5C6B73] mt-1 leading-snug">{d}</p></div></div>;
          })}
        </div>
      </Wrap>
    </section>
  );
}

export function Offer({ t, offer, cta, idea }) {
  const o = t.offer;
  return (
    <section className="bg-[#102B3F] text-white relative overflow-hidden" data-testid="landing-offer">
      <div className="absolute inset-0 lp-grid opacity-[0.07]" />
      <Wrap className="relative py-16 md:py-24 grid md:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
        <div className="order-2 md:order-1 py-4"><GuideMockup t={t} idea={idea} compact testid="offer-mockup" /></div>
        <div className="order-1 md:order-2">
          <div className="lp-eyebrow text-[#5FE0CB]">{o.eyebrow}</div>
          <h2 className="font-display font-extrabold text-[28px] leading-[1.12] sm:text-4xl lg:text-[44px] mt-3">{o.h}</h2>
          <p className="text-white/75 mt-4 text-base sm:text-lg">{o.p}</p>
          <div className="mt-6 text-xs font-bold uppercase tracking-wider text-white/50">{o.get}</div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2 mt-3">{o.list.map((x) => <li key={x} className="flex gap-2.5 text-[15px]"><Check className="h-4 w-4 text-[#0BAF9A] mt-0.5 shrink-0" strokeWidth={3} />{x}</li>)}</ul>
          <div className="mt-8 rounded-3xl bg-white/[0.06] border border-white/10 p-5 sm:p-6">
            <Price offer={offer} t={t} light size="lg" testid="offer-price" />
            <CtaButton onClick={() => cta("offer")} testid="landing-offer-cta" dark className="w-full mt-4">{o.cta} <ArrowRight className="h-5 w-5" /></CtaButton>
            <p className="text-xs text-white/60 mt-3 text-center">{o.under}</p>
          </div>
        </div>
      </Wrap>
    </section>
  );
}

const STEP_ICONS = [Send, CreditCard, KeyRound];
export function Process({ t, offer }) {
  const s = t.steps;
  const wa = offer?.whatsapp_number && `https://wa.me/${offer.whatsapp_number}?text=${encodeURIComponent(s.supportMsg)}`;
  return (
    <section className="bg-white" data-testid="landing-process">
      <Wrap className="py-16 md:py-24">
        <h2 className="lp-h2">{s.h}</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-9">
          {s.items.map(([title, d], i) => {
            const Icon = STEP_ICONS[i];
            return <div key={title} className="lp-card p-5 sm:p-6" data-testid={`process-step-${i}`}><div className="flex items-center justify-between"><span className="text-[11px] font-bold tracking-widest text-[#088F7E]">{s.step} {i + 1}</span><Icon className="h-5 w-5 text-[#0BAF9A]" /></div><div className="font-display font-bold text-lg text-[#102B3F] mt-3">{title}</div><p className="text-sm text-[#5C6B73] mt-1">{d}</p></div>;
          })}
        </div>
        {wa && <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-7 text-sm font-semibold text-[#102B3F] hover:text-[#088F7E] underline-offset-4 hover:underline" data-testid="landing-support-link"><MessageCircle className="h-4 w-4 text-[#25D366]" />{s.support}</a>}
      </Wrap>
    </section>
  );
}

export function Faq({ t }) {
  const [open, setOpen] = useState(-1);
  return (
    <section className="bg-[#EAF8F4]" data-testid="landing-faq-section">
      <Wrap className="py-16 md:py-24 max-w-3xl">
        <h2 className="lp-h2">{t.faq.h}</h2>
        <div className="mt-8 lp-card divide-y divide-[#E1ECEF]" data-testid="landing-faq">
          {t.faq.items.map(([q, a], i) => (
            <div key={q}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 p-5 text-left font-display font-bold text-[15px] text-[#102B3F]" data-testid={`faq-${i}`} aria-expanded={open === i}>{q}<ChevronDown className={`h-5 w-5 shrink-0 text-[#0BAF9A] transition-transform ${open === i ? "rotate-180" : ""}`} /></button>
              {open === i && <p className="px-5 pb-5 -mt-1 text-[15px] text-[#4A5D6A] leading-relaxed" data-testid={`faq-answer-${i}`}>{a}</p>}
            </div>
          ))}
        </div>
      </Wrap>
    </section>
  );
}

export function Final({ t, offer, cta }) {
  const f = t.final;
  return (
    <section className="bg-[#102B3F] text-white" data-testid="landing-final">
      <Wrap className="py-16 md:py-24 text-center max-w-3xl">
        <h2 className="font-display font-extrabold text-[30px] leading-[1.1] sm:text-5xl">{f.h}</h2>
        <p className="text-white/75 mt-4 text-base sm:text-lg">{f.p}</p>
        <div className="mt-8 flex justify-center"><Price offer={offer} t={t} light size="lg" testid="final-price" /></div>
        <CtaButton onClick={() => cta("final")} testid="landing-final-cta" dark className="mt-6 w-full sm:w-auto">{f.cta} <ArrowRight className="h-5 w-5" /></CtaButton>
        <p className="mt-10 text-xs text-white/50 flex items-start justify-center gap-2 max-w-xl mx-auto" data-testid="landing-disclaimer"><ShieldCheck className="h-4 w-4 shrink-0" />{f.disclaimer}</p>
        <p className="text-xs text-white/40 mt-6">© {new Date().getFullYear()} Sidebizz · Mauritius</p>
      </Wrap>
    </section>
  );
}

export function StickyBar({ t, offer, cta, hidden }) {
  const cd = useCountdown(offer?.promo_active ? offer.promo_expiry : null);
  if (hidden) return null;
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E1ECEF] pb-safe shadow-[0_-8px_24px_-12px_rgba(16,43,63,.25)]" data-testid="landing-sticky-bar">
      <div className="px-4 py-2.5 flex items-center gap-3">
        <div className="flex-1 min-w-0"><Price offer={offer} t={t} size="sm" testid="sticky-price" showExpiry={false} /></div>
        <div className="shrink-0 flex flex-col items-center">
          <button onClick={() => cta("sticky")} className="lp-btn min-h-[46px] px-5 text-sm" data-testid="landing-sticky-cta">{t.getGuide} <ArrowRight className="h-4 w-4" /></button>
          {cd && <div className="text-[11px] font-bold text-[#DC2626] mt-1 tabular-nums" data-testid="sticky-countdown">{t.price.ends} {cd}</div>}
        </div>
      </div>
    </div>
  );
}
